import { Component, EventEmitter, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Customers, Orders } from '../../interfaces';
import { DateTime } from 'luxon';
import { EditOrderDialogComponent } from '../../order-management/edit-order-dialog/edit-order-dialog.component';

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  orderCount: number;
}

@Component({
  selector: 'app-month-calendar',
  templateUrl: './month-calendar.component.html',
  styleUrl: './month-calendar.component.scss'
})
export class MonthCalendarComponent {
  @ViewChild(EditOrderDialogComponent) editOrderDialog!: EditOrderDialogComponent;

  @Input() displayedDate: Date = new Date();
  @Input() orders: Orders[] = [];
  @Input() customers: Customers[] = [];

  @Output() dateSelected = new EventEmitter<Date>();

  selectedDate: Date | null = null;
  calendarDays: CalendarDay[] = [];

  dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  showOrderDrawer: any;


  ngOnInit() {
    this.generateCalendar();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Regenerate calendar when displayedDate or orders change
    if (changes['displayedDate'] || changes['orders']) {
      // If orders changed, we want to update the calendar to reflect new order counts
      if (changes['orders']) {
        console.log('Orders updated, regenerating calendar');
      }
      this.generateCalendar();
    }
  }


  generateCalendar() {
    const year = this.displayedDate.getFullYear();
    const month = this.displayedDate.getMonth();

    // Get first day of the month and last day of the month
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Get first day of the calendar (might be from previous month)
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    // Get last day of the calendar (might be from next month)
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - lastDay.getDay()));

    // Generate calendar days
    this.calendarDays = [];
    const currentDateObj = new Date(startDate);

    while (currentDateObj <= endDate) {
      const isCurrentMonth = currentDateObj.getMonth() === month;
      const isToday = this.isToday(currentDateObj);
      const isSelected = this.selectedDate ? this.isSameDate(currentDateObj, this.selectedDate) : false;

      this.calendarDays.push({
        date: new Date(currentDateObj),
        isCurrentMonth,
        isToday,
        isSelected,
        orderCount: this.getOrderCount(currentDateObj) // Placeholder for future order integration
      });

      currentDateObj.setDate(currentDateObj.getDate() + 1);
    }
  }

  isToday(date: Date): boolean {
    const today = new Date();
    return this.isSameDate(date, today);
  }

  isSameDate(date1: Date, date2: Date): boolean {
    return date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear();
  }

  getOrderCount(date: Date) {
    return this.getOrdersForDate(date).length;
  }

  getOrdersForDate(date: Date): Orders[] {
    return this.orders.filter(order => {
      const orderDate = new Date(order.delivery_date);
      return this.isSameDate(date, orderDate);
    });
  }

  getCustomerName(customerId: number): string {
    const customer = this.customers.find(c => c.customer_id === customerId);
    return (customer ? customer.name.toUpperCase() : 'Unknown Customer');
  }

  selectDate(day: CalendarDay) {
    if (this.selectedDate) {
      // Remove selection from previously selected day
      const prevSelected = this.calendarDays.find(d => d.isSelected);
      if (prevSelected) {
        prevSelected.isSelected = false;
      }
    }

    this.selectedDate = day.date;
    day.isSelected = true;
    this.showOrderDrawer = true;
    this.dateSelected.emit(day.date);
  }

  toCentralTime(date: string | Date): string {
    const dt = DateTime.fromISO(date.toString(), { zone: 'utc' });
    const centralTime = dt.setZone('America/Chicago');
    // Check if the time is midnight in Central Time (meaning no specific time was selected)
    if (centralTime.hour === 0 && centralTime.minute === 0 && centralTime.second === 0) {
      return centralTime.toFormat('MM/dd/yyyy');
    }
    return centralTime.toFormat('MM/dd/yyyy h:mm a');
  }

}
